import React, { useEffect, useRef, useState } from 'react';

import { IoClose } from 'react-icons/io5';

import ButtonDefault from '@components/buttons/ButtonDefault';
import ButtonMain from '@components/buttons/ButtonMain';
import ButtonOutline from '@components/buttons/ButtonOutline';
import InputMask from '@components/inputs/InputMask';
import InputText from '@components/inputs/InputText';
import Paragraph from '@components/texts/Paragraph';
import SubtitleSecondary from '@components/texts/SubtitleSecondary';
import { ConfigBase, ConfigStorage } from '@config/index';
import {
  apiRebox,
  apiViaCep,
  newProviderStorageService,
} from '@services/index';
import { formatText } from '@utils/formatters';
import { toastify } from '@utils/notifiers';

import { IProviderAddress, ITarget } from './typing';

import {
  AddressModal,
  Container,
  FormProviderAddress,
  LinkSearchCep,
  Sections,
  SectionsGroup,
  SectionsItem,
} from './styles';

interface IProps {
  forNewProviderAddress?: {
    advanceStep(): void;
  };
}

const FormProviderNewAddress: React.FC<IProps> = ({
  forNewProviderAddress,
}) => {
  const formRef = useRef(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showAllAddressFields, setShowAllAddressFields] = useState<boolean>(
    false,
  );

  const [
    isShowAllMultipleAddresses,
    setIsShowAllMultipleAddress,
  ] = useState<boolean>(false);

  const [
    isFieldsAddressesEnabled,
    setIsFieldsAddressesEnabled,
  ] = useState<boolean>(false);

  const [
    isFieldsMultipleAddressesEnabled,
    setIsFieldsMultipleAddressesEnabled,
  ] = useState<boolean>(false);

  const [isOpenAddressModal, setIsOpenAddressModal] = useState<boolean>(false);

  const [
    providerAddressData,
    setProviderAddressData,
  ] = useState<IProviderAddress>({
    zip_code: '',
    street: '',
    city: '',
    complement: '',
    country: 'BR',
    neighborhood: '',
    number: '',
    state: '',
  });

  const [providerAddresses, setProviderAddresses] = useState<IProviderAddress>({
    zip_code: '',
    street: '',
    country: 'BR',
    city: '',
    complement: '',
    neighborhood: '',
    number: '',
    state: '',
  });

  const [multipleProviderAddresses, setMultipleProviderAddresses] = useState<
    IProviderAddress[]
  >([]);

  const handleChangeProviderAddress = ({ name, value }: ITarget) => {
    setProviderAddressData(prevAddress => ({
      ...prevAddress,
      [name]: value,
    }));
  };

  const handleChangeProviderAddresses = ({ name, value }: ITarget) => {
    setProviderAddresses(prevAddresses => ({
      ...prevAddresses,
      [name]: value,
    }));
  };

  const handleGetAddressByZipcode = async (zip_code: string) => {
    try {
      const { data: responseCep } = await apiViaCep.get(`/${zip_code}/json/`);
      const { uf, localidade, bairro, logradouro } = responseCep;

      setShowAllAddressFields(true);
      if (logradouro.length) {
        setProviderAddressData(prevAddress => ({
          ...prevAddress,
          state: uf,
          city: localidade,
          neighborhood: bairro,
          street: logradouro,
        }));
        setIsFieldsAddressesEnabled(false);
      } else {
        setProviderAddressData({
          ...providerAddressData,
          street: '',
          country: 'BR',
          city: '',
          complement: '',
          neighborhood: '',
          number: '',
          state: '',
        });
        setIsFieldsAddressesEnabled(true);
        setShowAllAddressFields(true);
      }
    } catch (error) {
      setShowAllAddressFields(true);
      setIsFieldsAddressesEnabled(true);
      toastify(
        'Não foi possível buscar o endereço pelo cep. Por favor tente novamente.',
        'error',
      );
    }
  };

  const handleGetAddressesByZipcode = async (zip_code: string) => {
    try {
      const { data: responseCep } = await apiViaCep.get(`/${zip_code}/json/`);
      const { uf, localidade, bairro, logradouro } = responseCep;

      setIsShowAllMultipleAddress(true);
      if (logradouro.length) {
        setProviderAddresses(prevAddress => ({
          ...prevAddress,
          state: uf,
          city: localidade,
          neighborhood: bairro,
          street: logradouro,
        }));
        setIsFieldsMultipleAddressesEnabled(false);
      } else {
        setProviderAddresses({
          ...providerAddresses,
          street: '',
          country: 'BR',
          city: '',
          complement: '',
          neighborhood: '',
          number: '',
          state: '',
        });
        setIsShowAllMultipleAddress(true);
        setIsFieldsMultipleAddressesEnabled(true);
      }
    } catch (error) {
      setIsShowAllMultipleAddress(true);
      setIsFieldsMultipleAddressesEnabled(true);
      toastify(
        'Não foi possível buscar o endereço pelo cep. Por favor tente novamente.',
        'error',
      );
    }
  };

  const handleAddAddresses = (event: any) => {
    event.preventDefault();
    if (providerAddresses.zip_code === '') {
      toastify('Por favor, insira o CEP', 'error');
    } else {
      setMultipleProviderAddresses([
        ...multipleProviderAddresses,
        {
          ...providerAddresses,
        },
      ]);
      setProviderAddresses({
        zip_code: '',
        street: '',
        country: 'BR',
        city: '',
        complement: '',
        neighborhood: '',
        number: '',
        state: '',
      });
      setIsFieldsMultipleAddressesEnabled(false);
      setIsOpenAddressModal(prev => !prev);
      toastify('Endereço adicionado com sucesso.', 'success');
    }
  };

  const handleSubmitAddresses = async () => {
    try {
      setIsLoading(true);
      if (providerAddressData.zip_code.length) {
        setMultipleProviderAddresses([
          ...multipleProviderAddresses,
          providerAddressData,
        ]);
      }
      setProviderAddressData({
        zip_code: '',
        street: '',
        country: 'BR',
        city: '',
        complement: '',
        neighborhood: '',
        number: '',
        state: '',
      });
      setShowAllAddressFields(false);

      const storagedUser = newProviderStorageService.getProvider(
        ConfigStorage.REBOX_REGISTER_NEW_PROVIDER,
      );
      let id = '';
      if (storagedUser) {
        id = JSON.parse(storagedUser).id;
      }

      for await (const address of multipleProviderAddresses) {
        await apiRebox.post('/users/address', {
          users_id: id,
          ...address,
        });
      }
      setIsLoading(false);
      forNewProviderAddress?.advanceStep();
    } catch (error: any) {
      setIsLoading(false);
      toastify(error.response.data.error, 'error');
    }
  };

  useEffect(() => {
    newProviderStorageService.updateAddresses(multipleProviderAddresses);
  }, [multipleProviderAddresses]);

  return (
    <Container>
      <FormProviderAddress ref={formRef} initialData={[]} onSubmit={() => []}>
        <Sections>
          <SectionsGroup>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                fontSize={16}
                nameColor="black"
              >
                CEP
              </SubtitleSecondary>
              <Paragraph
                textAlign="start"
                style={{ marginBottom: '2vh' }}
                nameColor="black"
                opacity={0.5}
              >
                Informe o código postal que localiza o endereço
              </Paragraph>
              <InputMask
                name="zip_code"
                placeholder="Informe o CEP"
                tabIndex={1}
                value={providerAddressData.zip_code}
                mask={'99999-999'}
                onChange={({ target }) => handleChangeProviderAddress(target)}
                onBlur={({ target }) => {
                  if (target.value.length === 0) {
                    setShowAllAddressFields(false);
                    setProviderAddressData({
                      zip_code: '',
                      street: '',
                      city: '',
                      country: 'BR',
                      complement: '',
                      neighborhood: '',
                      number: '',
                      state: '',
                    });
                  }
                  const cep = formatText.removeAllNonDigits(target.value);
                  if (cep.length === 8) {
                    handleGetAddressByZipcode(cep);
                  }
                }}
              />
              <LinkSearchCep
                to={{
                  pathname: ConfigBase.correio.baseUrls.buscaCepInter,
                }}
                key="config"
                target="_blank"
                tabIndex={2}
              >
                Não sei o cep
              </LinkSearchCep>
            </SectionsItem>
            {showAllAddressFields && (
              <SectionsItem>
                <SubtitleSecondary
                  textAlign="start"
                  nameColor="black"
                  fontSize={14}
                >
                  Estado
                </SubtitleSecondary>
                <Paragraph
                  nameColor="black"
                  textAlign="start"
                  opacity={0.5}
                  fontSize={13}
                  style={{ marginBottom: '2vh' }}
                >
                  Informe a UF do estado brasileiro
                </Paragraph>
                <InputText
                  name="state"
                  style={{
                    cursor: isFieldsAddressesEnabled ? 'text' : 'not-allowed',
                    opacity: isFieldsAddressesEnabled ? '1' : '0.5',
                  }}
                  placeholder="Informe o estado"
                  readOnly={!isFieldsAddressesEnabled}
                  tabIndex={3}
                  value={providerAddressData.state}
                  autoFocus
                  onChange={({ target }) => {
                    handleChangeProviderAddress(target);
                  }}
                />
              </SectionsItem>
            )}
          </SectionsGroup>
          {showAllAddressFields && (
            <>
              <SectionsGroup>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Cidade
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Informe o nome da cidade localizada no estado
                  </Paragraph>
                  <InputText
                    name="city"
                    value={providerAddressData.city}
                    readOnly={!isFieldsAddressesEnabled}
                    tabIndex={4}
                    style={{
                      cursor: isFieldsAddressesEnabled ? 'text' : 'not-allowed',
                      opacity: isFieldsAddressesEnabled ? '1' : '0.5',
                    }}
                    placeholder="Informe a cidade"
                    onChange={({ target }) => {
                      handleChangeProviderAddress(target);
                    }}
                  />
                </SectionsItem>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Bairro
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Informe o nome do bairro localizado na cidade
                  </Paragraph>
                  <InputText
                    name="neighborhood"
                    readOnly={!isFieldsAddressesEnabled}
                    tabIndex={5}
                    style={{
                      cursor: isFieldsAddressesEnabled ? 'text' : 'not-allowed',
                      opacity: isFieldsAddressesEnabled ? '1' : '0.5',
                    }}
                    value={providerAddressData.neighborhood}
                    onChange={({ target }) => {
                      handleChangeProviderAddress(target);
                    }}
                    placeholder="Informe o bairro"
                  />
                </SectionsItem>
              </SectionsGroup>

              <SectionsGroup>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Rua
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Informe o nome completo da rua
                  </Paragraph>
                  <InputText
                    name="street"
                    tabIndex={6}
                    style={{
                      cursor: isFieldsAddressesEnabled ? 'text' : 'not-allowed',
                      opacity: isFieldsAddressesEnabled ? '1' : '0.5',
                    }}
                    value={providerAddressData.street}
                    readOnly={!isFieldsAddressesEnabled}
                    onChange={({ target }) => {
                      handleChangeProviderAddress(target);
                    }}
                    placeholder="Informe a rua"
                  />
                </SectionsItem>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Número
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Informe o número da casa localizada na rua
                  </Paragraph>
                  <InputText
                    name="number"
                    tabIndex={7}
                    onChange={({ target }) => {
                      handleChangeProviderAddress(target);
                    }}
                    placeholder="Informe o número"
                  />
                </SectionsItem>
              </SectionsGroup>

              <SectionsGroup>
                <SectionsItem>
                  <SubtitleSecondary
                    textAlign="start"
                    nameColor="black"
                    fontSize={14}
                  >
                    Complemento
                  </SubtitleSecondary>
                  <Paragraph
                    nameColor="black"
                    textAlign="start"
                    opacity={0.5}
                    fontSize={13}
                    style={{ marginBottom: '2vh' }}
                  >
                    Informações adicionais, como: Ponto de referência, casa 1,
                    etc.
                  </Paragraph>
                  <InputText
                    name="complement"
                    tabIndex={8}
                    onChange={({ target }) => {
                      handleChangeProviderAddress(target);
                    }}
                    value={providerAddressData.complement.toUpperCase()}
                    placeholder="Complemento"
                  />
                </SectionsItem>
              </SectionsGroup>
            </>
          )}

          <SectionsGroup>
            <SectionsItem>
              <Paragraph
                textAlign="start"
                nameColor="black"
                opacity={0.8}
                fontWeight={500}
              >
                Você deseja adicionar uma base adjacente?
              </Paragraph>
              <ButtonOutline
                style={{
                  marginTop: '1rem',
                  maxWidth: '200px',
                }}
                onClick={event => {
                  event.preventDefault();
                  setIsOpenAddressModal(prev => !prev);
                  if (providerAddressData.zip_code.length) {
                    setMultipleProviderAddresses([
                      ...multipleProviderAddresses,
                      providerAddressData,
                    ]);
                  }
                  setProviderAddressData({
                    zip_code: '',
                    street: '',
                    country: 'BR',
                    city: '',
                    complement: '',
                    neighborhood: '',
                    number: '',
                    state: '',
                  });
                  setShowAllAddressFields(false);
                }}
              >
                Adicionar
              </ButtonOutline>
            </SectionsItem>
          </SectionsGroup>
          <ButtonMain
            type="submit"
            loading={isLoading}
            style={{ marginTop: '4vh', maxWidth: 250 }}
            tabIndex={9}
            onClick={event => {
              event.preventDefault();
              handleSubmitAddresses();
            }}
          >
            Continuar
          </ButtonMain>
        </Sections>
        <AddressModal
          isOpen={isOpenAddressModal}
          ariaHideApp={false}
          onRequestClose={() => {
            setIsOpenAddressModal(prev => !prev);
            setProviderAddresses({
              zip_code: '',
              street: '',
              country: 'BR',
              city: '',
              complement: '',
              neighborhood: '',
              number: '',
              state: '',
            });
            setIsFieldsMultipleAddressesEnabled(false);
            setIsShowAllMultipleAddress(false);
          }}
          contentLabel="AddressModal"
        >
          <ButtonDefault
            iconLeft={IoClose}
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              maxWidth: 50,
              padding: 0,
            }}
            onClick={() => {
              setIsOpenAddressModal(prev => !prev);
              setProviderAddresses({
                zip_code: '',
                street: '',
                country: 'BR',
                city: '',
                complement: '',
                neighborhood: '',
                number: '',
                state: '',
              });
              setIsFieldsMultipleAddressesEnabled(false);
              setIsShowAllMultipleAddress(false);
            }}
          />
          <Sections style={{ width: '70%', margin: '0 auto' }}>
            <SectionsItem>
              <SubtitleSecondary
                textAlign="start"
                nameColor="black"
                fontSize={14}
              >
                Base Adjacente
              </SubtitleSecondary>
              <InputMask
                name="zip_code"
                placeholder="Informe o CEP"
                mask="99999-999"
                value={providerAddresses.zip_code}
                onChange={({ target }) => {
                  handleChangeProviderAddresses(target);
                }}
                onBlur={({ target }) => {
                  if (target.value.length === 0) {
                    setIsShowAllMultipleAddress(false);
                    setProviderAddresses({
                      zip_code: '',
                      street: '',
                      country: 'BR',
                      city: '',
                      complement: '',
                      neighborhood: '',
                      number: '',
                      state: '',
                    });
                  }
                  const cep = formatText.removeAllNonDigits(target.value);
                  if (cep.length === 8) {
                    handleGetAddressesByZipcode(cep);
                  }
                }}
              />
              <LinkSearchCep
                to={{
                  pathname: ConfigBase.correio.baseUrls.buscaCepInter,
                }}
                key="config"
                target="_blank"
                tabIndex={2}
              >
                Não sei o cep
              </LinkSearchCep>
            </SectionsItem>
            {isShowAllMultipleAddresses && (
              <>
                <SectionsItem style={{ marginBottom: '2vh', marginTop: '2vh' }}>
                  <InputText
                    name="state"
                    style={{
                      cursor: isFieldsMultipleAddressesEnabled
                        ? 'text'
                        : 'not-allowed',
                      opacity: isFieldsMultipleAddressesEnabled ? '1' : '0.5',
                    }}
                    readOnly={!isFieldsMultipleAddressesEnabled}
                    placeholder="Informe o estado"
                    value={providerAddresses.state}
                    onChange={({ target }) => {
                      handleChangeProviderAddresses(target);
                    }}
                    autoFocus
                  />
                </SectionsItem>

                <SectionsItem style={{ marginBottom: '2vh' }}>
                  <InputText
                    name="city"
                    value={providerAddresses.city}
                    placeholder="Informe a cidade"
                    style={{
                      cursor: isFieldsMultipleAddressesEnabled
                        ? 'text'
                        : 'not-allowed',
                      opacity: isFieldsMultipleAddressesEnabled ? '1' : '0.5',
                    }}
                    readOnly={!isFieldsMultipleAddressesEnabled}
                    onChange={({ target }) => {
                      handleChangeProviderAddresses(target);
                    }}
                  />
                </SectionsItem>
                <SectionsItem style={{ marginBottom: '2vh' }}>
                  <InputText
                    name="neighborhood"
                    onChange={({ target }) => {
                      handleChangeProviderAddresses(target);
                    }}
                    value={providerAddresses.neighborhood}
                    placeholder="Informe o bairro"
                    style={{
                      cursor: isFieldsMultipleAddressesEnabled
                        ? 'text'
                        : 'not-allowed',
                      opacity: isFieldsMultipleAddressesEnabled ? '1' : '0.5',
                    }}
                    readOnly={!isFieldsMultipleAddressesEnabled}
                  />
                </SectionsItem>

                <SectionsItem style={{ marginBottom: '2vh' }}>
                  <InputText
                    name="street"
                    onChange={({ target }) => {
                      handleChangeProviderAddresses(target);
                    }}
                    value={providerAddresses.street}
                    placeholder="Informe a rua"
                    style={{
                      cursor: isFieldsMultipleAddressesEnabled
                        ? 'text'
                        : 'not-allowed',
                      opacity: isFieldsMultipleAddressesEnabled ? '1' : '0.5',
                    }}
                    readOnly={!isFieldsMultipleAddressesEnabled}
                  />
                </SectionsItem>

                <SectionsItem style={{ marginBottom: '2vh' }}>
                  <InputText
                    name="number"
                    onChange={({ target }) => {
                      handleChangeProviderAddresses(target);
                    }}
                    placeholder="Número"
                  />
                </SectionsItem>

                <SectionsItem style={{ marginBottom: '2vh' }}>
                  <InputText
                    name="complement"
                    onChange={({ target }) => {
                      handleChangeProviderAddresses(target);
                    }}
                    value={providerAddresses.complement.toUpperCase()}
                    placeholder="Complemento"
                  />
                </SectionsItem>
              </>
            )}
            <ButtonMain
              onClick={event => {
                handleAddAddresses(event);
              }}
            >
              Adicionar
            </ButtonMain>
          </Sections>
        </AddressModal>
      </FormProviderAddress>
    </Container>
  );
};

export default FormProviderNewAddress;
